import {BehaviorSubject, Observable} from 'rxjs';
import {Store} from '@ngxs/store';
import {Component, Input, OnInit, ViewChild, ChangeDetectionStrategy, ChangeDetectorRef, HostListener} from '@angular/core';
import {EntityListHeaderParams} from 'app/blocks/entity/entity-list-header-params';
import {DiSearchBarComponent} from 'app/common/di-search-bar/di-search-bar.component';
import {RESPONSIVE_SCREEN_SIZE} from 'app/app.constants';
import {ElasticSearchService} from 'app/blocks/service/api/elastic-search.service';
import {TranslateService} from '@ngx-translate/core';
import {DialogService} from 'app/common/dialog/dialog-service';
import {LogService} from 'app/blocks/service/log.service';
import {debounce} from 'lodash';

@Component({
    selector: 'di-list-page-header',
    templateUrl: './di-list-page-header.component.html',
    styleUrls: ['./di-list-page-header.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DiListPageHeaderComponent implements OnInit {
    @Input()
    headerParams: EntityListHeaderParams;

    @ViewChild(DiSearchBarComponent, {static: true})
    public readonly search: DiSearchBarComponent;

    selectedIds$: Observable<number[]>;
    groupActionProcessing$: Observable<any>;

    reindexingSubject = new BehaviorSubject<boolean>(false);
    reindexing$ = this.reindexingSubject.asObservable();

    headingKey: string;
    addButtonTextKey: string;
    reindexButtonTextKey: string;
    isSmallScreen: boolean;

    constructor(
        public readonly store: Store,
        private changeDetectorRef: ChangeDetectorRef,
        private reindexService: ElasticSearchService,
        private translateService: TranslateService,
        private dialogService: DialogService,
        private _logService: LogService
    ) {}

    ngOnInit(): void {
        this.headingKey = `${this.headerParams.resource.name}.pages.list.heading`;
        this.addButtonTextKey = `${this.headerParams.resource.name}.pages.list.buttons.add`;
        this.reindexButtonTextKey = `common.reindex.reindex`;
        this.selectedIds$ = this.store.select((state) => state.entities[this.headerParams.resource.name].listView.selectedIds);
        this.groupActionProcessing$ = this.headerParams.groupActionProcessing;
        this.handleResponsive();
    }

    private debouncedLog = debounce((action) => {
        this._logService.log(action, {
            actionGroup: 'List Page Header'
        });
    }, 500);

    reindex(): void {
        this.debouncedLog(`Reindex ${this.headerParams.resource.name}`);
        this.reindexingSubject.next(true);
        this.reindexService
            .postElasticSearchReIndexRequest(this.headerParams.resource.name, true)
            .then((r) => {
                this.reindexingSubject.next(false);
                this.translateService
                    .get('common.reindex.messages.ResultOK.message')
                    .pipe()
                    .subscribe((message: string) => {
                        if (message) {
                            this.dialogService.showMessage({
                                message: message,
                                confirmText: 'common.ok'
                            });
                        }
                    });
            })
            .catch((e) => {
                this.reindexingSubject.next(false);
                this.translateService
                    .get('common.reindex.messages.ResultFailed.message')
                    .pipe()
                    .subscribe((message: string) => {
                        if (message) {
                            this.dialogService.showMessage({
                                message: message,
                                confirmText: 'common.ok'
                            });
                        }
                    });
            });
    }

    getCurrentPageSelectedIds = () => {
        const listView = this.store.selectSnapshot((state) => state.entities[this.headerParams.resource.name].listView);
        const entityList: any[] = listView.entityList;
        const selectedIds: number[] = listView.selectedIds;
        return entityList.map((entity) => entity.id).filter((id) => selectedIds.includes(id));
    };

    @HostListener('window:resize', ['$event'])
    onResize(event?): void {
        this.handleResponsive();
        this.changeDetectorRef.detectChanges();
    }

    private handleResponsive(): void {
        if (window.innerWidth <= RESPONSIVE_SCREEN_SIZE) {
            this.isSmallScreen = true;
        } else {
            this.isSmallScreen = false;
        }
    }

    refresh(): void {
        this.debouncedLog(`Refresh ${this.headerParams.resource.name}`);
        this.headerParams.refreshAction();
    }

    addEntity(): void {
        this.debouncedLog(`Add ${this.headerParams.resource.name}`);
        this.headerParams.addAction();
    }

    groupAction(act: any): void {
        this.debouncedLog(`${act.label} ${this.headerParams.resource.name} (Action menu) `);
        act.action();
    }

    newTransactionAction(act: any): void {
        this.debouncedLog(`${act.label} ${this.headerParams.resource.name} (New Transaction)`);
        act.action();
    }
}
